// SplitScreen (TEMPLATES.md): pinned status bar, two regions side by side,
// segmented on phone, optional footer. Lineup, Matchup, trade builder.
//
// Each desktop region is a Panel, so it gets a real heading and can carry the
// zen/spreadsheet switch (DETAIL_LEVELS.md part 4: every section of every
// screen says what it is). Phone keeps the segmented control instead — the
// segment already names the region, and a panel header under it would say the
// same word twice.
//
// Drawn through the kit (LOOKS.md Part 7): the ratios are classes, and the
// tablet's handle opens the kit's sheet from the bottom.

import { useEffect, useState, type ReactNode } from 'react';
import { opensAsSheet } from '@fsp/theme';
import { Sheet } from '../kit/overlays';
import { Panel, ViewSwitch, type PanelView } from '../kit/Panel';
import { useDetail } from '../data/detail';
import { useTier } from '../shell/useTier';
import { useRemembered } from '../shell/tabMemory';

export interface SplitRegion {
  id: string;
  title: string;
  content: ReactNode;
  badge?: ReactNode;
  /**
   * The phone's segment is one word wide, so a badge of words wraps over its
   * title; this replaces `badge` there when given (round 3, N6) — and on a
   * tablet, whose two panels are each about as wide as a phone and where the
   * words pushed the panel's view switch past its edge (round 3's review,
   * SCREENS-7).
   */
  narrowBadge?: ReactNode;
  /**
   * The every-column body. Passing it grows the panel's zen/spreadsheet
   * switch; the detail level decides which pole it opens at.
   */
  sheet?: ReactNode;
}

export function SplitScreen({
  status,
  regions,
  ratio = [7, 5],
  footer,
  phoneDefault,
  show,
  lead = false,
}: {
  status?: ReactNode;
  regions: SplitRegion[];
  /** The two regions' widths: 7 to 5 (the default), or even. */
  ratio?: [7, 5] | [5, 5];
  /**
   * One region leads, alone and wide, and the rest are the side column's
   * tabs (desktop) or the handle's (tablet): the matchup's head to head,
   * which pairs both sides in one region. A phone is unchanged.
   */
  lead?: boolean;
  footer?: ReactNode;
  phoneDefault?: string;
  /**
   * A region the address asks for (`?show=log`): it wins over the one the
   * tab remembers, once, on a phone. A door to the transaction log lands on
   * the log, not on whatever was open last.
   */
  show?: string;
}) {
  const tier = useTier();
  const detail = useDetail();
  // KEPT BY THE TAB (UX-39): Matchup on Them, then My Team and back, is Them.
  // By address, which names the screen and the league.
  const [active, setActive] = useRemembered(
    `split:${window.location.pathname}`,
    phoneDefault ?? regions[0]?.id ?? '',
  );
  useEffect(() => {
    if (show !== undefined && regions.some((r) => r.id === show)) setActive(show);
    // Once per address, not per render: the regions are rebuilt every render.
  }, [show]);
  // Phone has no panel header to hang the zen/spreadsheet switch on, so it
  // lives beside the segmented control, which is the header here. Per region
  // and sticky per region, same as the desktop panel.
  const [views, setViews] = useState<Record<string, PanelView>>({});
  // Tablet: which of the regions past the first two is open in the sheet.
  const [sheetId, setSheetId] = useState<string | null>(null);
  const viewFor = (id: string): PanelView => views[id] ?? (opensAsSheet(detail) ? 'sheet' : 'zen');

  if (tier === 'phone') {
    const current = regions.find((r) => r.id === active) ?? regions[0]!;
    const currentView = viewFor(current.id);
    // THE SCREEN SCROLLS AS ONE (UX-38): the status scrolls away with the
    // list, and the segments that switch it stick to the top.
    return (
      <div className="fsp-screen fsp-flow">
        {status ? <div className="fsp-pinned">{status}</div> : null}
        <div className="fsp-pinned fsp-pinned-row fsp-pinned-stick">
          {/* ONE REGION IS NAMED, NOT TABBED: a one-option segmented control
              is a switch with nothing to switch to (My Team's lineup, B-215). */}
          {regions.length === 1 ? (
            <span className="fsp-cap fsp-split-title">{current.title}</span>
          ) : (
            <div className={`fsp-segments fsp-ui-cols-${regions.length}`} role="tablist">
              {regions.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={r.id === current.id}
                  className="fsp-segment"
                  onClick={() => setActive(r.id)}
                >
                  {r.title}
                  {r.narrowBadge ?? r.badge}
                </button>
              ))}
            </div>
          )}
          {current.sheet !== undefined ? (
            <ViewSwitch
              view={currentView}
              onChange={(v) => setViews((prev) => ({ ...prev, [current.id]: v }))}
              title={current.title}
            />
          ) : null}
        </div>
        <div className="fsp-regions fsp-board-one">
          <section className="fsp-region" aria-label={current.title}>
            {current.sheet !== undefined && currentView === 'sheet'
              ? current.sheet
              : current.content}
          </section>
        </div>
        {footer ? <div className="fsp-footer">{footer}</div> : null}
      </div>
    );
  }

  // TWO COLUMNS, NEVER THREE (DIRECTION U21, B-317). Past two regions a
  // desktop leads with the first and puts the rest, the second among them,
  // behind tabs in the column beside it, half and half: three columns of
  // lists side by side read as a wall of lists. A tablet keeps the first two
  // side by side and the rest behind its handle.
  const leads = lead || (tier !== 'tablet' && regions.length > 2);
  const even = !lead && leads;
  const shown = regions.slice(0, leads ? 1 : 2);
  const extra = regions.slice(leads ? 1 : 2);
  // One region shown takes the width: a two-column grid left it seven
  // twelfths of the screen with nothing beside it.
  const grid =
    shown.length === 1 && extra.length === 0
      ? 'fsp-board-one'
      : leads
        ? tier === 'tablet'
          ? 'fsp-board-one'
          : even
            ? 'fsp-split-5-5'
            : 'fsp-split-lead'
        : `fsp-split-${ratio[0]}-${ratio[1]}`;

  // TABLET: THE TWO REGIONS SIDE BY SIDE, THE REST BEHIND A HANDLE. A third
  // column at 834 left the matchup's name column about 50 pixels, so every
  // player's name broke to "Quins… Judkins" and his game line was cut
  // (the review of round 2, SCREENS-9). MATCHUP.md's tablet layout is the
  // rosters side by side with plays and talk as tabs on a bottom-sheet handle,
  // and WAIVERS.md makes the same call for its log.
  if (tier === 'tablet' && extra.length > 0) {
    const open = extra.find((e) => e.id === sheetId) ?? null;
    return (
      <div className="fsp-screen">
        {status ? <div className="fsp-pinned">{status}</div> : null}
        <div className={`fsp-regions ${grid}`}>
          {shown.map((r) => (
            <Panel key={r.id} title={r.title} meta={r.narrowBadge ?? r.badge} sheet={r.sheet} fill>
              {r.content}
            </Panel>
          ))}
        </div>
        <div className="fsp-footer fsp-handle">
          <div className={`fsp-segments fsp-segments-inline fsp-grow fsp-ui-cols-${extra.length}`}>
            {extra.map((r) => (
              <button
                key={r.id}
                type="button"
                className="fsp-segment"
                aria-haspopup="dialog"
                aria-expanded={open?.id === r.id}
                onClick={() => setSheetId(r.id)}
              >
                {r.title}
                {r.badge}
              </button>
            ))}
          </div>
        </div>
        {footer ? <div className="fsp-footer">{footer}</div> : null}
        <Sheet
          opened={open !== null}
          onClose={() => setSheetId(null)}
          title={open?.title}
          from="bottom"
        >
          {open?.content}
        </Sheet>
      </div>
    );
  }

  const extraCurrent = extra.find((e) => e.id === active) ?? extra[0];
  return (
    <div className="fsp-screen">
      {status ? <div className="fsp-pinned">{status}</div> : null}
      <div className={`fsp-regions ${grid}`}>
        {shown.map((r) => (
          <Panel
            key={r.id}
            title={r.title}
            meta={tier === 'tablet' ? (r.narrowBadge ?? r.badge) : r.badge}
            sheet={r.sheet}
            fill
          >
            {r.content}
          </Panel>
        ))}
        {extraCurrent ? (
          // The third column is a stack of small regions behind tabs. The panel
          // is titled by whichever is showing, and the tabs sit in the header
          // where the control belongs.
          <Panel
            title={extraCurrent.title}
            fill
            meta={
              // One region needs no tabs: the panel's title already names it.
              extra.length < 2 ? undefined : (
                <div
                  className={`fsp-segments fsp-segments-inline fsp-ui-cols-${extra.length}`}
                  role="tablist"
                >
                  {extra.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="tab"
                      aria-selected={r.id === extraCurrent.id}
                      className="fsp-segment"
                      onClick={() => setActive(r.id)}
                    >
                      {r.title}
                      {r.badge}
                    </button>
                  ))}
                </div>
              )
            }
          >
            {extraCurrent.content}
          </Panel>
        ) : null}
      </div>
      {footer ? <div className="fsp-footer">{footer}</div> : null}
    </div>
  );
}
